"use client"
import { useState } from "react"
import { useNavigate } from 'react-router-dom';
import { Mail, Eye, EyeOff } from "lucide-react"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import logo from "../asset/img/logo-full.png"
import bg from "../asset/img/bg.jpg"
import { useAuth } from "../context/AuthContext"; // Import useAuth hook
import toast from "react-hot-toast";
import { ForgotPasswordModal } from "../components/forgot-assword-modal" // adjust path as needed

export default function LoginForm() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false)

  const navigate = useNavigate()
  // Use the auth context instead of local state for auth-related functionality
  const { login, loading } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Password length validation
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    const result = await login(email, password);
    if (result.success) {
      setSuccessMessage("Login Successful!")
      // Short delay to show success message before redirect
      setTimeout(() => {
        toast.success("Welcome back! Login successful");
        const user = localStorage.getItem('user');
        const parsed = user ? JSON.parse(user) : {};
        const role = parsed?.role;
        const perms = parsed?.permissions;
        const isAdmin = String(role || "").trim().toLowerCase() === "admin";

        if (isAdmin || perms?.analytics_overview) {
          navigate("/");
        } else if (perms?.tournament_leaderboard && !perms?.promos) {
          navigate("/tournament-leaderboard");
        } else {
          navigate("/promos");
        }
      }, 1000)
    } else {
      // Handle specific error cases
      if (result.error || result.message) {
        const errorMessage = (result.error || result.message).toLowerCase();
        
        // Handle 401 Unauthorized errors specifically
        if (errorMessage.includes('401') || errorMessage.includes('unauthorized')) {
          toast.error("Invalid email or password. Please check your credentials and try again.");
        }
        // Handle other specific HTTP errors
        else if (errorMessage.includes('400') || errorMessage.includes('bad request')) {
          toast.error("Invalid request. Please check your input and try again.");
        }
        else if (errorMessage.includes('403') || errorMessage.includes('forbidden')) {
          toast.error("Access denied. Your account may be suspended.");
        }
        else if (errorMessage.includes('404') || errorMessage.includes('not found')) {
          toast.error("Service not available. Please try again later.");
        }
        else if (errorMessage.includes('500') || errorMessage.includes('server error')) {
          toast.error("Server error. Please try again in a few moments.");
        }
        else if (errorMessage.includes('503') || errorMessage.includes('service unavailable')) {
          toast.error("Service temporarily unavailable. Please try again later.");
        }
        // Handle specific authentication errors
        else if (errorMessage.includes('user not found') || errorMessage.includes('email') || errorMessage.includes('account')) {
          toast.error("No account found with this email address");
        } 
        else if (errorMessage.includes('password') || errorMessage.includes('incorrect') || errorMessage.includes('invalid')) {
          toast.error("Incorrect password. Please try again");
        } 
        else if (errorMessage.includes('blocked') || errorMessage.includes('suspended')) {
          toast.error("Your account has been suspended. Contact support");
        } 
        else if (errorMessage.includes('network') || errorMessage.includes('connection')) {
          toast.error("Network error. Please check your connection");
        } 
        else if (errorMessage.includes('too many attempts') || errorMessage.includes('rate limit')) {
          toast.error("Too many login attempts. Please try again later");
        } 
        else {
          // Generic error message for unhandled cases
          toast.error("Login failed. Please check your credentials and try again.");
        }
      } else {
        // Fallback when no specific error message is provided
        toast.error("Login failed. Please check your credentials and try again.");
      }
    }
  }

  const handleForgotPassword = (email) => {
    // You can call your API here
    toast.success(`Password reset link sent to ${email}`)
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  // Handle input validation on blur
  const handleEmailBlur = () => {
    if (email && !email.includes('@')) {
      toast.error("Please enter a valid email address");
    }
  }

  const handlePasswordBlur = () => {
    if (password && password.length < 6) {
      toast.error("Password must be at least 6 characters long");
    }
  }

  return (
    <div
      className="flex items-center justify-center min-h-screen w-full bg-black"
      style={{ backgroundImage: `url(${bg})`, backgroundSize: "cover" }}
    >
      {/* Full screen loader overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-black/90 border border-white/20 p-5 rounded-lg flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-lg font-semibold text-white">Logging you in...</p>
          </div>
        </div>
      )}

      <div className="w-full max-w-md px-8 py-10 mx-4 rounded-lg border border-white/20 bg-black/80 backdrop-blur-sm">
        <div className="flex justify-center mb-8">
          <div className="text-center space-y-4">
            <img
              src={logo}
              alt="Elite Poker Lounge McAllen"
              className="w-56 sm:w-64 h-auto object-contain mx-auto"
            />
            <p className="text-sm text-amber-400/90 font-medium">
              Dashboard &amp; Analytics
            </p>
          </div>
        </div>

        {/* Remove the error display div since we're using toasts now */}
        {successMessage && (
          <div
            className="bg-green-900/30 border border-green-500/50 text-green-200 px-4 py-3 rounded-md mb-6"
            role="alert"
          >
            <span className="block text-sm">{successMessage}</span>
          </div>
        )}

        <form className="space-y-8" onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div className="relative">
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={handleEmailBlur}
                className="w-full bg-transparent border-b border-t-0 border-x-0 rounded-none px-0 py-2 text-white placeholder:text-white/70 focus-visible:ring-0 focus-visible:border-white"
              />
              <Mail className="absolute right-0 top-2.5 h-5 w-5 text-white" />
            </div>

            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={handlePasswordBlur}
                className="w-full bg-transparent border-b border-t-0 border-x-0 rounded-none px-0 py-2 text-white placeholder:text-white/70 focus-visible:ring-0 focus-visible:border-white"
              />
              <button type="button" className="absolute right-0 top-2.5 text-white" onClick={togglePasswordVisibility}>
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowForgotPasswordModal(true)}
            className="text-sm text-white hover:text-amber-400 transition-colors"
          >
            Forgot Password?
          </button>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-white hover:bg-white/90 text-black font-medium py-6 rounded-full"
          >
            {loading ? "Signing in..." : "Login"}
          </Button>

        </form>
        <ForgotPasswordModal
          isOpen={showForgotPasswordModal}
          onClose={() => setShowForgotPasswordModal(false)}
          onSubmit={handleForgotPassword}
        />

      </div>
    </div>
  )
}